import { Skeleton, TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Box } from '@mui/material';
import React from 'react';
import {
	CerebroBreadcrumb,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroPageContainer,
} from '../../shared/components';
import { useDataRoomDirectConnectMilestoneQuery } from '../../shared/hooks';
import { DataRoom, DataRoomUser, DirectConnectMilestoneReport } from '../../shared/models';
import { OptOutForm } from '../Participants/Lenders/OptOutForm';
import { SendEmailForm } from '../Participants/Lenders/SendEmailForm';
import { ViewContactDetails } from '../Participants/Lenders/ViewContactDetails';
import { DirectConnectMilestoneDetails } from './DirectConnectMilestoneDetails';
import { DirectConnectMilestoneRow } from './DirectConnectMilestoneRow';
import { SendLenderEmailsForm } from './SendLenderEmailsForm';
import { EmailLendersActionButton } from './EmailLendersActionButton';

interface DirectConnectMilestonePageProps {
	dataRoom: DataRoom;
}

export const DirectConnectMilestonePage: React.FC<DirectConnectMilestonePageProps> = ({ dataRoom }) => {
	const dataRoomGuid = dataRoom.guid;
	const milestoneQuery = useDataRoomDirectConnectMilestoneQuery(dataRoomGuid);
	const isLoading = milestoneQuery.isLoading;
	const isFetching = milestoneQuery.isRefetching || milestoneQuery.isFetching || isLoading;
	const isSuccess = milestoneQuery.isSuccess;

	const [milestoneDetail, setMilestoneDetail] = React.useState<DirectConnectMilestoneReport | null>(null);
	const [userBeingOptedOut, setUserBeingOptedOut] = React.useState<DataRoomUser | null>(null);
	const [sendEmail, setSendEmail] = React.useState<DataRoomUser | null>(null);
	const [viewContactDetails, setViewContactDetails] = React.useState<DataRoomUser | null>(null);
	const [sendLenderEmailsOpen, setSendLenderEmailsOpen] = React.useState<boolean>(false);

	const [filter, setFilter] = React.useState<string>('active');

	const onChangeFilter = (target: string) => {
		setFilter(target);
	};

	const clearDialogsAndDrawers = (): void => {
		setMilestoneDetail(null);
		setUserBeingOptedOut(null);
		setSendEmail(null);
		setViewContactDetails(null);
		setSendLenderEmailsOpen(false);
	};

	return (
		<CerebroPageContainer maxWidth="100%">
			<CerebroBreadcrumb>Lender Progress</CerebroBreadcrumb>

			<Box sx={{ marginBottom: 2 }}>
				<CerebroDropdown
					sx={{ marginRight: 1, width: '15%' }}
					value={filter}
					onChange={data => onChangeFilter(data.target.value as string)}
				>
					<CerebroDropdownMenuItem value="all">All Lenders</CerebroDropdownMenuItem>
					<CerebroDropdownMenuItem value="active">Active Lenders</CerebroDropdownMenuItem>
					<CerebroDropdownMenuItem value="inactive">Inactive Lenders</CerebroDropdownMenuItem>
				</CerebroDropdown>
				<EmailLendersActionButton
					setAll={() => {
						setFilter('all');
						setSendLenderEmailsOpen(true);
					}}
					setActive={() => {
						setFilter('active');
						setSendLenderEmailsOpen(true);
					}}
					setInactive={() => {
						setFilter('inactive');
						setSendLenderEmailsOpen(true);
					}}
				/>
			</Box>

			<TableContainer>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell sx={{ width: '5px', padding: '0px' }}></TableCell>
							<TableCell></TableCell>
							<TableCell>Lender Name</TableCell>
							<TableCell sx={{ textAlign: 'center' }}>Documents Downloaded</TableCell>
							<TableCell sx={{ textAlign: 'center' }}>Term Sheet Submitted</TableCell>
							<TableCell sx={{ textAlign: 'center' }}>Underwriting</TableCell>
							<TableCell sx={{ textAlign: 'center' }}>Funded</TableCell>
							<TableCell></TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{isFetching && (
							<>
								<TableRow>
									<TableCell colSpan={8}>
										<Skeleton height={60} />
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell colSpan={8}>
										<Skeleton height={60} />
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell colSpan={8}>
										<Skeleton height={60} />
									</TableCell>
								</TableRow>
							</>
						)}
						{isSuccess && (
							<>
								{milestoneQuery.data
									.sort((a, b) => a.bankName.localeCompare(b.bankName))
									.filter(
										item =>
											filter == 'all' ||
											(filter == 'active' && item.activeUser == true) ||
											(filter == 'inactive' && item.activeUser == false)
									)
									.map(milestone => (
										<DirectConnectMilestoneRow
											key={milestone.guid}
											milestone={milestone}
											dataRoom={dataRoom}
											onDetails={setMilestoneDetail}
											optOutUser={setUserBeingOptedOut}
											sendEmail={setSendEmail}
											contactDetails={setViewContactDetails}
										/>
									))}
							</>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			<DirectConnectMilestoneDetails report={milestoneDetail} close={clearDialogsAndDrawers} />

			<SendLenderEmailsForm
				dataRoomGuid={dataRoomGuid}
				open={sendLenderEmailsOpen}
				milestones={milestoneQuery.data ?? []}
				statusFilter={filter as 'active' | 'inactive' | 'all'}
				close={clearDialogsAndDrawers}
			/>
			<OptOutForm dataRoomGuid={dataRoomGuid} user={userBeingOptedOut} close={clearDialogsAndDrawers} />
			<SendEmailForm dataRoomGuid={dataRoomGuid} user={sendEmail} close={clearDialogsAndDrawers} />
			<ViewContactDetails roomGuid={dataRoomGuid} roomUser={viewContactDetails} close={clearDialogsAndDrawers} />
		</CerebroPageContainer>
	);
};
